import client from "./client";
import type { ConsentLocale } from "../lib/consentLocale";

// iam-stc consent API (plan 2026-11-02 privacy consent, Task 6). Texts live on the public legal site, one page per
// consent type: the API gives title, summary, labels and the page url; "More" opens the url, nothing is rendered here.

export type TopicKey =
  | "terms"
  | "privacy_notice"
  | "crash_reports"
  | "usage_analytics"
  | "external_media"
  | "contacts_sync"
  | "map_position_public"
  | "live_stream_notice";
export type Decision = "ACCEPTED" | "DECLINED" | "ACKNOWLEDGED";
export type Source =
  | "FIRST_START"
  | "SIGN_UP"
  | "RECONSENT"
  | "JUST_IN_TIME"
  | "SETTINGS"
  | "DEVICE_LINK";
export type ConsentKind = "REQUIRED" | "OPTIONAL" | "NOTICE";
export type ConsentScope = "ACCOUNT" | "DEVICE";

/** Device items the web asks (usage_analytics is Android only). */
export const WEB_DEVICE_TOPICS = ["crash_reports", "external_media"] as const;
export type WebDeviceTopic = (typeof WEB_DEVICE_TOPICS)[number];

export interface ConsentTopic {
  key: TopicKey;
  kind: ConsentKind;
  scope: ConsentScope;
  versionId: string;
  version: number;
  textId: string;
  title: string;
  summary: string;
  acceptLabel: string;
  declineLabel: string;
  /** The type's page on the legal site; "More" opens it in a new tab. */
  url: string;
  decision: Decision | null;
  needsAnswer: boolean;
  updated: boolean;
  effectiveAt: string;
}

export interface ConsentOverview {
  topics: ConsentTopic[];
  pending: TopicKey[];
  consentPending: boolean;
}

export interface DecisionIn {
  versionId: string;
  textId: string;
  decision: Decision;
  source: Source;
  clientAt: string;
}

export interface DecisionResult {
  topicKey: TopicKey;
  recorded: boolean;
}

export interface DecisionsOut {
  results: DecisionResult[];
  consentPending: boolean;
}

export interface HistoryItem {
  eventId: string;
  topicKey: TopicKey;
  versionId: string;
  version: number;
  textId: string;
  decision: Decision;
  source: Source;
  platform: "WEB" | "ANDROID";
  appVersion: string;
  siteCommit: string;
  clientAt: string | null;
  recordedAt: string;
}

export interface ConsentDocument {
  versionId: string;
  topicKey: TopicKey;
  version: number;
  material: boolean;
  effectiveAt: string;
  locale: ConsentLocale;
  title: string;
  summary: string;
  url: string;
}

const BASE = "/iam/api/v1/consent";

export async function getConsent(locale: ConsentLocale): Promise<ConsentOverview> {
  const { data } = await client.get<ConsentOverview>(BASE, {
    params: { locale, platform: "WEB" },
  });
  return data;
}

function isNetworkError(e: unknown): boolean {
  return e instanceof Error && !(e as { response?: unknown }).response;
}

/**
 * One batch = one POST. A network error is retried once with the same body (the server treats a resent batch as
 * the same answers). Any HTTP error is thrown: a 503 means the server could not read the legal site's commit and
 * recorded nothing — the caller says "try again", never treats it as accepted.
 */
export async function postDecisions(decisions: DecisionIn[]): Promise<DecisionsOut> {
  const body = { platform: "WEB", appVersion: __APP_VERSION__, decisions };
  try {
    const { data } = await client.post<DecisionsOut>(`${BASE}/decisions`, body);
    return data;
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    const { data } = await client.post<DecisionsOut>(`${BASE}/decisions`, body);
    return data;
  }
}

/** True for a failure the user should simply retry (503: nothing recorded, or no network). */
export function isConsentTryAgain(e: unknown): boolean {
  const status = (e as { response?: { status?: number } } | null)?.response?.status;
  return status === undefined || status === 503;
}

export async function getHistory(before?: string): Promise<HistoryItem[]> {
  const { data } = await client.get<{ items: HistoryItem[] }>(`${BASE}/history`, {
    params: before ? { before } : {},
  });
  return data.items;
}

export async function getDocument(
  versionId: string,
  locale: ConsentLocale,
): Promise<ConsentDocument> {
  const { data } = await client.get<ConsentDocument>(
    `${BASE}/versions/${encodeURIComponent(versionId)}`,
    { params: { locale } },
  );
  return data;
}

/** REQUIRED/OPTIONAL: ticked → ACCEPTED, else DECLINED; NOTICE → ACKNOWLEDGED. */
export function toDecisions(
  topics: ConsentTopic[],
  ticked: Set<TopicKey>,
  source: Source,
): DecisionIn[] {
  const clientAt = new Date().toISOString();
  return topics.map((t) => ({
    versionId: t.versionId,
    textId: t.textId,
    decision:
      t.kind === "NOTICE" ? "ACKNOWLEDGED" : ticked.has(t.key) ? "ACCEPTED" : "DECLINED",
    source,
    clientAt,
  }));
}
